import { Link as RouterLink } from 'react-router-dom';

import Button from 'components/partials/button/button';
import { Chip } from 'components/partials/insight-modal/shared/components';

import { formatDate } from 'helpers/dates-formatter';

import Icons from 'assets/icons';

import type { InsightSummary } from 'containers/admin/clients/client/tabs-components/insights-tab/hooks/use-insights';

interface CellArgs {
  row: { original: InsightSummary };
}

interface InsightsTableSettingsArgs {
  clientId: string | number | undefined;
}

interface InsightsColumnDef {
  id: string;
  accessorKey?: string;
  header?: () => React.JSX.Element;
  cell: (args: CellArgs) => React.ReactNode;
  headerClassName?: string;
  className?: string;
  enableSorting?: boolean;
  sortingFn?: string;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold';

const unknownToStringArray = (value: unknown): string[] | undefined => {
  if (
    !Array.isArray(value) ||
    !value.every((item): item is string => typeof item === 'string')
  ) {
    return undefined;
  }

  return value;
};

const unknownToString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const formatRelatedGoals = (
  relatedGoals: string[] | undefined,
): React.ReactNode => {
  if (
    !relatedGoals ||
    !Array.isArray(relatedGoals) ||
    relatedGoals.length === 0
  ) {
    return <span className="text-gray-600">No related goals</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {relatedGoals.map((goal, idx) => (
        <Chip key={`goal-${idx}`}>{goal}</Chip>
      ))}
    </div>
  );
};

const getSourceIcon = (
  insightType: string | undefined,
): React.ReactNode => {
  if (insightType === 'report') {
    return <Icons.Reports customClass="size-5 pt-1" />;
  }
  if (insightType === 'monitoring') {
    return <Icons.Chart customClass="size-5 pt-1" />;
  }

  return null;
};

export const getInsightsTableSettings = ({
  clientId,
}: InsightsTableSettingsArgs): InsightsColumnDef[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <span>INSIGHT NAME</span>,
    cell: ({ row }) => {
      const insight = row.original;

      return (
        <div className="flex flex-col py-3">
          <span className="w-full pr-2 pb-1 font-semibold">
            {insight.name || 'Untitled Insight'}
          </span>
        </div>
      );
    },
    headerClassName: `${commonStyles} cursor-pointer rounded-tl-md rounded-bl-md`,
    className: 'pl-4 tablet:w-[20%]',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  },
  {
    id: 'relatedGoals',
    accessorKey: 'metadata.relatedGoals',
    header: () => <span>RELATED GOALS</span>,
    cell: ({ row }) => {
      const insight = row.original;
      const relatedGoals = unknownToStringArray(
        insight.metadata?.relatedGoals,
      );

      return formatRelatedGoals(relatedGoals);
    },
    headerClassName: `${commonStyles}`,
    className: 'pl-4 tablet:w-[25%] py-3',
    enableSorting: false,
  },
  {
    id: 'source',
    accessorKey: 'metadata.insightType',
    header: () => <span>SOURCE</span>,
    cell: ({ row }) => {
      const insight = row.original;
      const insightType = unknownToString(insight.metadata?.insightType);
      const source = unknownToString(insight.metadata?.source);

      return (
        <div className="flex gap-2">
          <div className="shrink-0"> {getSourceIcon(insightType)}</div>
          <div>{source}</div>
        </div>
      );
    },
    headerClassName: `${commonStyles}`,
    className: 'pl-4 py-3 tablet:w-[25%]',
    enableSorting: false,
  },
  {
    id: 'createdDate',
    accessorKey: 'createdDate',
    header: () => <span>LAST UPDATED</span>,
    cell: ({ row }) => {
      const insight = row.original;

      return (
        <span className="text-sm">
          {formatDate(insight.createdDate, 'MMM D, YYYY h:mm A')}
        </span>
      );
    },
    headerClassName: `${commonStyles} cursor-pointer`,
    className: 'pl-4 tablet:w-[15%]',
    enableSorting: true,
  },
  {
    id: 'button-cell',
    cell: ({ row }) => {
      const insight = row.original;

      return (
        <RouterLink to={`/admin/clients/${clientId}/insights/${insight.id}`}>
          <Button
            variant="reserve-outlined"
            size="sm"
            iconBefore={<Icons.Eye aria-label="EyeIcon" />}
            className="text-xs md:text-sm"
          >
            Details
          </Button>
        </RouterLink>
      );
    },
    headerClassName: 'h-12 rounded-tr-md rounded-br-md',
    className: 'py-3 pl-4',
    enableSorting: false,
  },
];
